/* eslint-disable react/jsx-props-no-spreading -- wrappers here forward props wholesale */
import { jsx, Global, css } from '@emotion/react';
import loadable from '@loadable/component';

import LocationModalController from 'components/location/Popup/Modal';
import _ from 'lodash';
import { Component, Fragment } from 'react';
import ReactOnRails from 'react-on-rails';
import { Provider } from 'react-redux';
import { Route, Switch } from 'react-router-dom';

import { ConfigurationConnector } from './BYRConnectors';
import { logChangeData } from './analytics';
import { Header, Disclaimers } from './header_footer';
import { Loader } from './loading';
import { model3dSetTexture } from './model3dBridge';
import { NotSupported, NotSupportedNavigator } from './not_supported';
import ScrollToTop from './scrollTop.jsx';
import { SelectShingles } from './select_shingles';
import { SelectStep } from './step';
import { InitialRedirector } from './steps_order';
import { Summary } from './summary';
import { Section, StepColumns } from './utilities';
import { Ventilation } from './ventilation';
import 'iframe-resizer/js/iframeResizer.contentWindow.min';
import FeatureFlagged from '../FeatureFlag/FeatureFlagged';
import RailsContext, { withRailsContext } from '../RailsContext';
import RouterWithSSR from '../RouterWithSSR';
import { canUseDOM } from '../helpers/dom';

// three.js loads client-side only; the server renders the picker UI around it
const Model3D = loadable(() => import('./model3d').then(m => ({ default: m.Model3D })), {
  ssr: false,
  fallback: <Loader />,
});
const updateModel = (...args) => import('./model3d').then(m => m.updateModel(...args));

function handleFirstTab(e) {
  if (e.keyCode === 9) {
    document.body.classList.add('user-is-tabbing');

    window.removeEventListener('keydown', handleFirstTab);
    window.addEventListener('mousedown', handleMouseDownOnce);
  }
}

function handleMouseDownOnce() {
  document.body.classList.remove('user-is-tabbing');

  window.removeEventListener('mousedown', handleMouseDownOnce);
  window.addEventListener('keydown', handleFirstTab);
}

if (typeof window !== 'undefined') {
  window.addEventListener('keydown', handleFirstTab);
}

export class RawBuildYourRoof extends Component {
  constructor(props) {
    super();

    // We're setting default shingle and color
    let defaultShingle = _.get(props.shingles, '[0].items[0]', {});
    let defaultColor =
      _.find(defaultShingle.colors, {
        filename: 'trudefinition-duration-estate-gray',
      }) || {};

    // If configuration is given we override basic defaults
    if (props.configuration.custom) {
      const recommendedProducts = props.configuration.products.filter(p => p.recommended).map(p => p.uid);
      const recommendedShingles = props.shingles
        .map(sg => sg.items)
        .flat()
        .filter(s => recommendedProducts.includes(s.uid));

      if (recommendedShingles.length > 0) {
        defaultShingle = recommendedShingles[0];
        defaultColor = defaultShingle.colors[0];
      }
    }

    this.state = {
      loading: false,
      selected_shingle: [defaultShingle, defaultColor],
      selected_ventilation: 'ridge_vents',
      shingles: props.shingles || {},
    };
  }

  render() {
    const handlers = {
      setShingle: (shingle, color) => () => {
        this.setState({ loading: true });
        logChangeData('shingles', { shingle, color });

        model3dSetTexture('Shingles', color.filename).then(() => this.setState({ loading: false }));

        // Changing Shingle Line selection changes A LOT. Thus we're nuking all selection to make sure we're not keeping
        // product as selected even though it's no longer compatible etc.
        const newState = {};
        Object.keys(this.state)
          .filter(sk => sk.startsWith('selected_'))
          .forEach(k => (newState[k] = ''));
        newState.selected_shingle = [shingle, color];

        this.setState(newState);
      },
      set: (step, value) => {
        const newState = {};
        newState[`selected_${step}`] = value;

        return () => {
          this.setState({ loading: true });
          logChangeData(step, value);
          updateModel(step, value, true).then(() => this.setState({ loading: false }));
          this.setState(newState);
        };
      },
    };
    // Regexp inside is made to incorporate the match.
    // It works both with locale (/XX-YY/roofing/build-your-roof) as well as without it
    const basenameRegexp = /(\/[a-z]{2}-[a-z]{2})?\/roofing\/build-your-roof/;
    const pathname = canUseDOM() ? window.location.pathname : this.context?.pathname || '';
    let baseURL = (pathname.match(basenameRegexp) || [''])[0];

    const { slug } = this.props.configuration; // _.get(store.getState(), 'byrConfig.slug');

    if (slug) {
      baseURL += `/${slug}`;
    }

    const selectedColor = _.get(this.state, 'selected_shingle[1]', {});
    const selectedShingle = _.get(this.state, 'selected_shingle[0]', {});
    const location = canUseDOM() ? window.location : { pathname, search: '' };
    return (
      <>
        <RouterWithSSR basename={baseURL}>
          <NotSupportedNavigator />
          <Switch>
            <Route path={['/not-supported/:what', '/not-supported']}>
              <Section>
                <NotSupported locationChange={this.openLocationModal} location={location} />
              </Section>
            </Route>
            <Route path="/" exact>
              <InitialRedirector />
            </Route>

            <Route path="/summary">
              <ScrollToTop />
              <Header
                menu
                price="3"
                shingle={selectedShingle}
                locationChange={this.openLocationModal}
                location={location}
              />
              <Summary data={this.state} />
            </Route>

            <Route path={['/roof-:roofid']}>
              <Summary summary_only />
            </Route>
            <Route>
              <Header
                menu
                price="3"
                shingle={selectedShingle}
                locationChange={this.openLocationModal}
                location={location}
              />

              <Section>
                <StepColumns>
                  <div className="col">
                    <Switch>
                      <Route path="/shingles">
                        <ScrollToTop />
                        <SelectShingles
                          handlers={handlers}
                          shingles={this.state.shingles}
                          selected_color={selectedColor}
                          selected_shingle={selectedShingle}
                        />
                      </Route>
                      <Route path="/ice-water">
                        <ScrollToTop />
                        <SelectStep
                          handlers={handlers}
                          products={this.props.products}
                          step="ice-water"
                          key="ice-water"
                          state={this.state}
                        />
                      </Route>
                      <Route path="/underlayment">
                        <ScrollToTop />
                        <SelectStep
                          handlers={handlers}
                          products={this.props.products}
                          key="underlayment"
                          step="underlayment"
                          state={this.state}
                        />
                      </Route>
                      <Route path="/starter-shingles">
                        <ScrollToTop />
                        <SelectStep
                          handlers={handlers}
                          products={this.props.products}
                          key="starter-shingles"
                          step="starter-shingles"
                          state={this.state}
                        />
                      </Route>
                      <Route path="/hip-ridge">
                        <ScrollToTop />
                        <SelectStep
                          handlers={handlers}
                          products={this.props.products}
                          key="hip-ridge"
                          step="hip-ridge"
                          state={this.state}
                        />
                      </Route>
                      <Route path="/ventilation">
                        <ScrollToTop />
                        <Ventilation
                          handlers={handlers}
                          products={this.props.products}
                          step="ventilation"
                          state={this.state}
                        />
                      </Route>
                    </Switch>
                  </div>

                  <div className="col">
                    <Model3D data={this.state} loading={this.state.loading} />
                  </div>
                </StepColumns>

                <Disclaimers location={location} />
              </Section>
            </Route>
          </Switch>
        </RouterWithSSR>
        <LocationModalController showLocale showZip />
        <Global
          styles={css`
            body .page,
            .oc-footer {
              @media (max-width: 1199px) {
                max-width: none;
              }
            }

            body:not(.user-is-tabbing) * {
              outline: none;
            }

            body:not(.user-is-tabbing) .oc-cta-button:focus {
              color: white;

              &:before {
                content: '';
                background: #d40f7d;
                left: -1px;
                right: 0;
                transform: translateX(-100%);
                transition: none;
              }
            }

            @media print {
              .page {
                width: 100%;
                margin: 0;
                padding: 0;
              }
              footer {
                background: none !important;
                padding: 0 !important;
              }
              footer .copyright-statement {
                padding: 0;
                margin: 0;
              }
              footer .copyright-statement a {
                padding: 0;
              }
              .footer-content ul,
              .footer-util,
              .oc-cta-button {
                display: none;
              }
            }
          `}
        />
        <FeatureFlagged only={['contractor_byr']}>
          <Global
            styles={css`
              .cx-widget .cx-webchat {
                display: none;
              }

              .oc-flex-space,
              .oc-footer {
                display: none !important;
              }
            `}
          />
        </FeatureFlagged>
      </>
    );
  }
}

RawBuildYourRoof.contextType = RailsContext;

const BuildYourRoof = ConfigurationConnector(RawBuildYourRoof);
const WithStore = props => {
  const store = ReactOnRails.getStore('Store');

  return (
    <Provider store={store}>
      <BuildYourRoof {...props} />
    </Provider>
  );
};
export default withRailsContext(WithStore);
